import { useState, useRef } from "react";
import axios from "axios";
import { Upload, X, Check, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { SERVICE_OPTIONS, BRAND } from "@/data/content";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MAX_PHOTOS = 4;
const MAX_PHOTO_BYTES = 4 * 1024 * 1024; // 4MB each

const fileToBase64 = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

export default function BookingForm() {
  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    address: "",
    service: SERVICE_OPTIONS[0],
    description: "",
    preferred_date: "",
  });
  const [photos, setPhotos] = useState([]); // {name, dataUrl}
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const fileInputRef = useRef(null);

  const onChange = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onPickFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    const next = [...photos];
    for (const f of files) {
      if (next.length >= MAX_PHOTOS) {
        toast.error(`Maximum ${MAX_PHOTOS} photos`);
        break;
      }
      if (f.size > MAX_PHOTO_BYTES) {
        toast.error(`${f.name} is over 4MB`);
        continue;
      }
      const dataUrl = await fileToBase64(f);
      next.push({ name: f.name, dataUrl });
    }
    setPhotos(next);
    e.target.value = "";
  };

  const removePhoto = (i) => setPhotos(photos.filter((_, idx) => idx !== i));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.email || !form.address || !form.description) {
      toast.error("Please complete all required fields.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/leads`, {
        ...form,
        photos: photos.map((p) => p.dataUrl),
      });
      setDone(true);
      toast.success("Estimate request received. We'll be in touch shortly.");
    } catch (err) {
      console.error(err);
      toast.error("Something went wrong. Please call us at " + BRAND.phone);
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <section
        id="estimate"
        data-testid="estimate-section"
        className="relative bg-[#0E0E10] text-white py-32 md:py-44"
      >
        <div className="max-w-3xl mx-auto px-6 md:px-10 text-center">
          <div className="w-20 h-20 rounded-full bg-[#C8A44D] mx-auto flex items-center justify-center">
            <Check size={32} strokeWidth={1.5} className="text-[#111]" />
          </div>
          <h2 className="font-serif-display mt-10 text-4xl md:text-6xl tracking-tight">
            Request received.
          </h2>
          <p className="mt-6 text-white/70 font-light text-lg max-w-xl mx-auto">
            Thank you, <span className="text-[#C8A44D]">{form.name.split(" ")[0]}</span>.
            We&apos;ve got your details and will reach out within hours to confirm
            your estimate.
          </p>
          <a
            href={`tel:${BRAND.phoneRaw}`}
            data-testid="post-submit-call"
            className="mt-10 inline-flex btn-gold px-8 py-4 rounded-full text-sm font-medium"
          >
            Need it sooner? Call {BRAND.phone}
          </a>
        </div>
      </section>
    );
  }

  return (
    <section
      id="estimate"
      data-testid="estimate-section"
      className="relative bg-[#0E0E10] text-white py-24 md:py-36 overflow-hidden"
    >
      <div className="absolute inset-0 grain opacity-50 pointer-events-none" />
      <div className="max-w-5xl mx-auto px-6 md:px-10 relative">
        <div className="max-w-2xl mb-16">
          <div className="eyebrow">Request Estimate</div>
          <h2 className="font-serif-display mt-4 text-4xl md:text-6xl leading-[1.05] tracking-tight">
            Tell us about <span className="italic text-[#C8A44D]">your project</span>.
          </h2>
          <p className="mt-6 text-white/65 font-light leading-relaxed">
            Share a few details and photos. We'll respond within hours with a
            transparent, honest quote.
          </p>
        </div>        <form onSubmit={submit} className="grid md:grid-cols-2 gap-x-10 gap-y-7">
          {[
            ["name", "Full Name", "text", true],
            ["phone", "Phone", "tel", true],
            ["email", "Email", "email", true],
            ["address", "Address", "text", true],
          ].map(([k, label, type, required]) => (
            <div key={k} className="relative">
              <label className="block text-[10px] tracking-[0.3em] uppercase text-white/50 mb-2">
                {label}{required && " *"}
              </label>
              <input
                data-testid={`form-${k}`}
                type={type}
                value={form[k]}
                onChange={onChange(k)}
                required={required}
                className="input-luxe text-white placeholder:text-white/40"
                style={{ borderBottomColor: "rgba(255,255,255,0.18)" }}
                placeholder={label}
              />
            </div>
          ))}

          <div>
            <label className="block text-[10px] tracking-[0.3em] uppercase text-white/50 mb-2">
              Service Needed *
            </label>
            <select
              data-testid="form-service"
              value={form.service}
              onChange={onChange("service")}
              className="input-luxe text-white bg-[#0E0E10]"
              style={{ borderBottomColor: "rgba(255,255,255,0.18)" }}
            >
              {SERVICE_OPTIONS.map((opt) => (
                <option key={opt} value={opt} className="bg-[#0E0E10]">
                  {opt}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-[10px] tracking-[0.3em] uppercase text-white/50 mb-2">
              Preferred Date
            </label>
            <input
              data-testid="form-date"
              type="date"
              value={form.preferred_date}
              onChange={onChange("preferred_date")}
              className="input-luxe text-white"
              style={{ borderBottomColor: "rgba(255,255,255,0.18)", colorScheme: "dark" }}
            />
          </div>

          <div className="md:col-span-2">
            <label className="block text-[10px] tracking-[0.3em] uppercase text-white/50 mb-2">
              Project Description *
            </label>
            <textarea
              data-testid="form-description"
              value={form.description}
              onChange={onChange("description")}
              required
              rows={4}
              className="input-luxe text-white resize-none"
              style={{ borderBottomColor: "rgba(255,255,255,0.18)" }}
              placeholder="What needs to be done?"
            />
          </div>

          <div className="md:col-span-2">
            <label className="block text-[10px] tracking-[0.3em] uppercase text-white/50 mb-3">
              Photos (optional · up to {MAX_PHOTOS})
            </label>
            <div className="flex items-center gap-4 flex-wrap">
              <button
                type="button"
                data-testid="form-upload-btn"
                onClick={() => fileInputRef.current?.click()}
                className="inline-flex items-center gap-2 px-5 py-3 rounded-full border border-white/20 text-sm text-white hover:border-[#C8A44D] hover:text-[#C8A44D] transition-colors"
              >
                <Upload size={15} strokeWidth={1.5} />
                Upload Photos
              </button>
              <input
                ref={fileInputRef}
                data-testid="form-file-input"
                type="file"
                accept="image/*"
                multiple
                onChange={onPickFiles}
                className="hidden"
              />
              {photos.map((p, i) => (
                <div
                  key={i}
                  data-testid={`photo-thumb-${i}`}
                  className="relative w-16 h-16 rounded-lg overflow-hidden border border-white/15"
                >
                  <img src={p.dataUrl} alt={p.name} className="w-full h-full object-cover" />
                  <button
                    type="button"
                    data-testid={`photo-remove-${i}`}
                    onClick={() => removePhoto(i)}
                    className="absolute -top-1 -right-1 bg-black/80 rounded-full p-1"
                  >
                    <X size={12} strokeWidth={2} className="text-white" />
                  </button>
                </div>
              ))}
            </div>
          </div>

          <div className="md:col-span-2 mt-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-6">
            <p className="text-white/50 text-sm font-light max-w-md">
              By submitting, you agree to be contacted about your project. We
              never share your information.
            </p>
            <button
              type="submit"
              disabled={submitting}
              data-testid="form-submit"
              className="btn-gold inline-flex items-center justify-center gap-2 px-10 py-4 rounded-full text-sm font-medium tracking-wide disabled:opacity-60"
            >
              {submitting ? (
                <>
                  <Loader2 size={16} strokeWidth={1.75} className="animate-spin" />
                  Sending...
                </>
              ) : (
                "Submit Request"
              )}
            </button>
          </div>
        </form>
      </div>
    </section>
  );
}
